import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { servicesCatalogService } from '../../services/catalogService';
import {
  serviceLinkedPartnersService,
  serviceLifecycleService,
  serviceUpdatesService,
} from '../../services/serviceProfileService';
import { useToast } from '../../contexts/ToastContext';

// ── Helpers ────────────────────────────────────────────────────────────────

const getCategoryColor = (cat) => {
  const map = {
    design: 'bg-violet-100 text-violet-700',
    supply: 'bg-blue-100 text-blue-700',
    installation: 'bg-cyan-100 text-cyan-700',
    commissioning: 'bg-teal-100 text-teal-700',
    support: 'bg-amber-100 text-amber-700',
    'managed-services': 'bg-indigo-100 text-indigo-700',
    consulting: 'bg-pink-100 text-pink-700',
    training: 'bg-green-100 text-green-700',
  };
  return map?.[cat] || 'bg-slate-100 text-slate-600';
};

const getStatusColor = (status) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    sunset: 'bg-amber-100 text-amber-700',
    draft: 'bg-slate-100 text-slate-600',
  };
  return map?.[status] || 'bg-slate-100 text-slate-600';
};

const getDeliveryColor = (model) => {
  const map = {
    'partner-led': 'bg-blue-50 text-blue-600',
    joint: 'bg-purple-50 text-purple-600',
    'ecity-led': 'bg-green-50 text-green-600',
  };
  return map?.[model] || 'bg-slate-50 text-slate-600';
};

const LIFECYCLE_STAGES = ['draft', 'active', 'review', 'sunset'];

const STAGE_META = {
  draft: { label: 'Draft', icon: 'FileEdit', color: 'bg-slate-400', light: 'bg-slate-50 text-slate-600 border-slate-200' },
  active: { label: 'Active', icon: 'CheckCircle2', color: 'bg-emerald-500', light: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  review: { label: 'Under Review', icon: 'ClipboardList', color: 'bg-amber-500', light: 'bg-amber-50 text-amber-700 border-amber-200' },
  sunset: { label: 'Sunset', icon: 'Sunset', color: 'bg-slate-400', light: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const UPDATE_TYPE_META = {
  general: { label: 'General', color: 'bg-slate-100 text-slate-600', icon: 'Info' },
  sla_update: { label: 'SLA Update', color: 'bg-blue-100 text-blue-700', icon: 'Shield' },
  new_version: { label: 'New Version', color: 'bg-teal-100 text-teal-700', icon: 'RefreshCw' },
  eol_notice: { label: 'EOL Notice', color: 'bg-red-100 text-red-700', icon: 'AlertTriangle' },
  cert_change: { label: 'Cert Change', color: 'bg-amber-100 text-amber-700', icon: 'Award' },
};

const UPDATE_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'sla_update', label: 'SLA Update' },
  { value: 'new_version', label: 'New Version' },
  { value: 'eol_notice', label: 'EOL Notice' },
  { value: 'cert_change', label: 'Certification Change' },
];

const TIER_COLOR = {
  Strategic: 'bg-purple-100 text-purple-700',
  Gold: 'bg-yellow-100 text-yellow-700',
  Silver: 'bg-slate-100 text-slate-600',
  Tactical: 'bg-blue-100 text-blue-700',
};

const CATEGORY_LABELS = {
  design: 'Design', supply: 'Supply', installation: 'Installation',
  commissioning: 'Commissioning', support: 'Support',
  'managed-services': 'Managed Services', consulting: 'Consulting', training: 'Training',
};

const DELIVERY_LABELS = {
  'partner-led': 'Partner-Led', joint: 'Joint', 'ecity-led': 'eCity-Led',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Post Update Modal ──────────────────────────────────────────────────────

function PostUpdateModal({ serviceId, onClose, onSuccess }) {
  const [form, setForm] = useState({ updateType: 'general', title: '', content: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.title?.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const created = await serviceUpdatesService?.create(serviceId, form);
      onSuccess(created);
    } catch (err) {
      setError(err?.message || 'Failed to post update.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-16">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-24 py-20 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-900">Post Service Update</h3>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-24 space-y-16">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Update Type</label>
            <select
              className="w-full border border-slate-200 rounded-lg px-12 py-10 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
              value={form?.updateType}
              onChange={e => setForm(p => ({ ...p, updateType: e?.target?.value }))}
            >
              {UPDATE_TYPES?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Title *</label>
            <input
              className="w-full border border-slate-200 rounded-lg px-12 py-10 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
              placeholder="Brief summary of the update..."
              value={form?.title}
              onChange={e => setForm(p => ({ ...p, title: e?.target?.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Details</label>
            <textarea
              className="w-full border border-slate-200 rounded-lg px-12 py-10 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 resize-none"
              rows={4}
              placeholder="Additional details..."
              value={form?.content}
              onChange={e => setForm(p => ({ ...p, content: e?.target?.value }))}
            />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex items-center gap-12 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-8 px-20 py-10 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >
              {saving && <Icon name="Loader2" size={14} className="animate-spin" />}
              Post Update
            </button>
            <button type="button" onClick={onClose} className="px-20 py-10 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Add Lifecycle Stage Modal ──────────────────────────────────────────────

function AddStageModal({ serviceId, onClose, onSuccess }) {
  const [form, setForm] = useState({ stage: 'active', stageDate: new Date()?.toISOString()?.split('T')?.[0], notes: '', expectedEolDate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await serviceLifecycleService?.addStage(serviceId, form);
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to add stage.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-16">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-24 py-20 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-900">Add Lifecycle Stage</h3>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-24 space-y-16">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Stage</label>
            <select
              className="w-full border border-slate-200 rounded-lg px-12 py-10 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
              value={form?.stage}
              onChange={e => setForm(p => ({ ...p, stage: e?.target?.value }))}
            >
              {LIFECYCLE_STAGES?.map(s => <option key={s} value={s}>{STAGE_META?.[s]?.label || s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Stage Date</label>
            <input
              type="date"
              className="w-full border border-slate-200 rounded-lg px-12 py-10 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
              value={form?.stageDate}
              onChange={e => setForm(p => ({ ...p, stageDate: e?.target?.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Notes</label>
            <textarea
              className="w-full border border-slate-200 rounded-lg px-12 py-10 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 resize-none"
              rows={3}
              placeholder="Stage notes..."
              value={form?.notes}
              onChange={e => setForm(p => ({ ...p, notes: e?.target?.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-6">Expected EOL Date</label>
            <input
              type="date"
              className="w-full border border-slate-200 rounded-lg px-12 py-10 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
              value={form?.expectedEolDate}
              onChange={e => setForm(p => ({ ...p, expectedEolDate: e?.target?.value }))}
            />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex items-center gap-12 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-8 px-20 py-10 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >
              {saving && <Icon name="Loader2" size={14} className="animate-spin" />}
              Add Stage
            </button>
            <button type="button" onClick={onClose} className="px-20 py-10 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Service Profile Page ──────────────────────────────────────────────

export default function ServiceProfile() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [service, setService] = useState(null);
  const [partners, setPartners] = useState([]);
  const [lifecycle, setLifecycle] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showPostUpdate, setShowPostUpdate] = useState(false);
  const [showAddStage, setShowAddStage] = useState(false);
  const [unassignConfirm, setUnassignConfirm] = useState(null);
  const [deleteUpdateConfirm, setDeleteUpdateConfirm] = useState(null);

  const loadAll = useCallback(async () => {
    if (!serviceId) return;
    setLoading(true);
    try {
      const [svc, pts, lc, upd] = await Promise.all([
        servicesCatalogService?.getAll()?.then ? servicesCatalogService?.getAll() : Promise.resolve([]),
        serviceLinkedPartnersService?.getByService(serviceId),
        serviceLifecycleService?.getByService(serviceId),
        serviceUpdatesService?.getByService(serviceId),
      ]);
      // getAll returns array — find by id
      const found = Array.isArray(svc) ? svc?.find(s => s?.id === serviceId) : null;
      setService(found || null);
      setPartners(pts || []);
      setLifecycle(lc || []);
      setUpdates(upd || []);
    } catch (err) {
      toast?.error('Failed to load service profile.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleUnassign = async (linkId) => {
    try {
      await serviceLinkedPartnersService?.unlink(linkId);
      setPartners(prev => prev?.filter(p => p?.id !== linkId));
      toast?.success('Partner unassigned from service.');
    } catch (err) {
      toast?.error('Failed to unassign partner.');
    } finally {
      setUnassignConfirm(null);
    }
  };

  const handleDeleteUpdate = async (id) => {
    try {
      await serviceUpdatesService?.delete(id);
      setUpdates(prev => prev?.filter(u => u?.id !== id));
      toast?.success('Update deleted.');
    } catch (err) {
      toast?.error('Failed to delete update.');
    } finally {
      setDeleteUpdateConfirm(null);
    }
  };

  // Determine current lifecycle stage
  const currentStage = lifecycle?.length > 0 ? lifecycle?.[lifecycle?.length - 1]?.stage : service?.catalogStatus || 'draft';
  const currentStageIdx = LIFECYCLE_STAGES?.indexOf(currentStage);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/20 to-cyan-50/20">
        <Header />
        <main className="pt-[148px] flex items-center justify-center min-h-[60vh]">
          <div className="flex items-center gap-12 text-slate-500">
            <Icon name="Loader2" size={22} className="animate-spin" />
            <span>Loading service profile...</span>
          </div>
        </main>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/20 to-cyan-50/20">
        <Header />
        <main className="pt-[148px] flex flex-col items-center justify-center min-h-[60vh] gap-16">
          <Icon name="Wrench" size={48} className="text-slate-300" />
          <p className="text-slate-500 text-lg">Service not found.</p>
          <Button variant="outline" onClick={() => navigate('/services-catalog')}>Back to Catalog</Button>
        </main>
      </div>
    );
  }

  const TABS = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'partners', label: `Partners (${partners?.length})`, icon: 'Users' },
    { id: 'lifecycle', label: 'Lifecycle', icon: 'GitBranch' },
    { id: 'updates', label: `Updates (${updates?.length})`, icon: 'Rss' },
  ];

  return (
    <>
      <Helmet>
        <title>{service?.serviceName || 'Service Profile'} — PartnerHub</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/20 to-cyan-50/20">
        <Header />

        <main className="pt-[148px] pb-32 px-24 lg:px-32">
          <div className="max-w-[1400px] mx-auto">

            {/* Breadcrumb */}
            <div className="flex items-center gap-8 mb-24 text-sm text-slate-500">
              <Link to="/services-catalog" className="hover:text-teal-600 transition-colors flex items-center gap-6">
                <Icon name="Wrench" size={14} />
                Services Catalog
              </Link>
              <Icon name="ChevronRight" size={14} />
              <span className="text-slate-800 font-medium">{service?.serviceName}</span>
            </div>

            {/* Service Header Card */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-24">
              <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-32 py-28">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-20">
                  <div className="flex items-start gap-20">
                    <div className="p-14 bg-white/20 rounded-xl backdrop-blur-sm">
                      <Icon name="Wrench" size={28} className="text-white" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-10 mb-10">
                        <span className={`inline-flex px-10 py-4 rounded-md text-xs font-semibold bg-white/20 text-white`}>
                          {CATEGORY_LABELS?.[service?.serviceCategory] || service?.serviceCategory}
                        </span>
                        <span className={`inline-flex px-10 py-4 rounded-md text-xs font-semibold bg-white/20 text-white`}>
                          {DELIVERY_LABELS?.[service?.deliveryModel] || service?.deliveryModel}
                        </span>
                        <span className={`inline-flex px-10 py-4 rounded-full text-xs font-semibold capitalize ${getStatusColor(service?.catalogStatus)}`}>
                          {service?.catalogStatus}
                        </span>
                      </div>
                      <h1 className="text-2xl lg:text-3xl font-bold text-white mb-8">{service?.serviceName}</h1>
                      {service?.description && (
                        <p className="text-white/80 text-sm max-w-2xl">{service?.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-10 flex-shrink-0">
                    <button
                      onClick={() => navigate('/services-catalog')}
                      className="flex items-center gap-8 px-16 py-10 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-lg transition-colors backdrop-blur-sm"
                    >
                      <Icon name="ArrowLeft" size={15} />
                      Back
                    </button>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 divide-x divide-slate-100 border-t border-slate-100">
                {[
                  { label: 'Partners Offering', value: partners?.length, icon: 'Users', color: 'text-teal-600' },
                  { label: 'Lifecycle Stage', value: STAGE_META?.[currentStage]?.label || currentStage, icon: 'GitBranch', color: 'text-amber-600' },
                  { label: 'Updates Posted', value: updates?.length, icon: 'Rss', color: 'text-blue-600' },
                ]?.map(stat => (
                  <div key={stat?.label} className="px-24 py-18 flex items-center gap-14">
                    <div className={`p-10 rounded-lg bg-slate-50`}>
                      <Icon name={stat?.icon} size={18} className={stat?.color} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">{stat?.label}</p>
                      <p className={`text-xl font-bold ${stat?.color}`}>{stat?.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-4 mb-24 border-b border-slate-200 overflow-x-auto">
              {TABS?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-8 px-16 py-12 text-sm font-medium border-b-2 transition-colors whitespace-nowrap -mb-px ${
                    activeTab === tab?.id
                      ? 'border-teal-600 text-teal-700' :'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Icon name={tab?.icon} size={15} />
                  {tab?.label}
                </button>
              ))}
            </div>

            {/* ── Overview Tab ── */}
            {activeTab === 'overview' && (
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-28">
                <h2 className="text-lg font-semibold text-slate-900 mb-20">Service Specifications</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-20">
                  {[
                    { label: 'Category', value: CATEGORY_LABELS?.[service?.serviceCategory] || service?.serviceCategory, icon: 'Tag' },
                    { label: 'Delivery Model', value: DELIVERY_LABELS?.[service?.deliveryModel] || service?.deliveryModel, icon: 'Truck' },
                    { label: 'Status', value: service?.catalogStatus, icon: 'Activity' },
                    { label: 'SLA Levels', value: service?.slaLevels, icon: 'Shield' },
                    { label: 'Certifications Required', value: service?.certificationsRequired, icon: 'Award' },
                    { label: 'Industry Alignment', value: service?.industryAlignment, icon: 'Building2' },
                  ]?.map(spec => (
                    <div key={spec?.label} className="p-16 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-8 mb-8">
                        <Icon name={spec?.icon} size={14} className="text-slate-400" />
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{spec?.label}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-800 capitalize">{spec?.value || '—'}</p>
                    </div>
                  ))}
                </div>
                {service?.description && (
                  <div className="mt-20 p-16 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-8 mb-8">
                      <Icon name="FileText" size={14} className="text-slate-400" />
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</span>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{service?.description}</p>
                  </div>
                )}
              </div>
            )}

            {/* ── Partners Tab ── */}
            {activeTab === 'partners' && (
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Linked Partners</h2>
                    <p className="text-sm text-slate-500 mt-2">Partners currently offering this service</p>
                  </div>
                </div>
                {partners?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-48 text-slate-400">
                    <Icon name="Users" size={36} className="mb-12 opacity-30" />
                    <p className="font-medium">No partners linked</p>
                    <p className="text-sm mt-4">Assign partners via the Portfolio Mapping interface</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-20 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Partner</th>
                          <th className="px-20 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Tier</th>
                          <th className="px-20 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Coverage Area</th>
                          <th className="px-20 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Delivery Capability</th>
                          <th className="px-20 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">SLA Commitment</th>
                          <th className="px-20 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                          <th className="px-20 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Linked</th>
                          <th className="px-20 py-12 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {partners?.map(partner => (
                          <tr key={partner?.id} className="hover:bg-slate-50/60 transition-colors">
                            <td className="px-20 py-14">
                              <Link
                                to={`/partner-profile-portal/${partner?.partnerId}`}
                                className="text-sm font-semibold text-teal-700 hover:text-teal-800 hover:underline"
                              >
                                {partner?.partnerName}
                              </Link>
                              {partner?.partnerCountry && (
                                <p className="text-xs text-slate-400 mt-1">{partner?.partnerCountry}</p>
                              )}
                            </td>
                            <td className="px-20 py-14">
                              {partner?.partnerTier ? (
                                <span className={`inline-flex px-8 py-4 rounded-md text-xs font-medium ${TIER_COLOR?.[partner?.partnerTier] || 'bg-slate-100 text-slate-600'}`}>
                                  {partner?.partnerTier}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="px-20 py-14 text-sm text-slate-500">{partner?.coverageArea || '—'}</td>
                            <td className="px-20 py-14 text-sm text-slate-500">{partner?.deliveryCapability || '—'}</td>
                            <td className="px-20 py-14 text-sm text-slate-500">{partner?.slaCommitment || '—'}</td>
                            <td className="px-20 py-14">
                              <span className={`inline-flex px-8 py-4 rounded-full text-xs font-medium capitalize ${
                                partner?.linkStatus === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {partner?.linkStatus || 'active'}
                              </span>
                            </td>
                            <td className="px-20 py-14 text-sm text-slate-400">{formatDate(partner?.createdAt)}</td>
                            <td className="px-20 py-14 text-right">
                              {unassignConfirm === partner?.id ? (
                                <div className="flex items-center justify-end gap-6">
                                  <button
                                    onClick={() => handleUnassign(partner?.id)}
                                    className="px-10 py-5 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    onClick={() => setUnassignConfirm(null)}
                                    className="px-10 py-5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setUnassignConfirm(partner?.id)}
                                  className="flex items-center gap-6 px-12 py-6 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 border border-red-200 hover:border-red-300 transition-colors"
                                >
                                  <Icon name="UserMinus" size={13} />
                                  Unassign
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── Lifecycle Tab ── */}
            {activeTab === 'lifecycle' && (
              <div className="space-y-20">
                {/* Stage Progress */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-28">
                  <div className="flex items-center justify-between mb-24">
                    <h2 className="text-lg font-semibold text-slate-900">Service Lifecycle</h2>
                    <button
                      onClick={() => setShowAddStage(true)}
                      className="flex items-center gap-8 px-16 py-9 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors"
                    >
                      <Icon name="Plus" size={15} />
                      Add Stage
                    </button>
                  </div>

                  {/* Visual Stage Track */}
                  <div className="flex items-center gap-0 mb-28 overflow-x-auto pb-4">
                    {LIFECYCLE_STAGES?.map((stage, idx) => {
                      const meta = STAGE_META?.[stage];
                      const isActive = stage === currentStage;
                      const isPast = LIFECYCLE_STAGES?.indexOf(stage) < currentStageIdx;
                      return (
                        <React.Fragment key={stage}>
                          <div className={`flex flex-col items-center gap-8 flex-shrink-0 min-w-[100px]`}>
                            <div className={`w-40 h-40 rounded-full flex items-center justify-center border-2 transition-all ${
                              isActive ? `${meta?.color} border-transparent shadow-lg` :
                              isPast ? 'bg-teal-500 border-transparent' : 'bg-white border-slate-200'
                            }`}>
                              <Icon
                                name={isActive || isPast ? meta?.icon : 'Circle'}
                                size={16}
                                className={isActive || isPast ? 'text-white' : 'text-slate-300'}
                              />
                            </div>
                            <span className={`text-xs font-medium text-center ${isActive ? 'text-teal-700' : isPast ? 'text-slate-600' : 'text-slate-400'}`}>
                              {meta?.label}
                            </span>
                            {isActive && (
                              <span className="text-xs text-teal-600 font-semibold bg-teal-50 px-8 py-2 rounded-full">Current</span>
                            )}
                          </div>
                          {idx < LIFECYCLE_STAGES?.length - 1 && (
                            <div className={`flex-1 h-2 min-w-[40px] rounded-full mx-4 ${
                              idx < currentStageIdx ? 'bg-teal-400' : 'bg-slate-200'
                            }`} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Stage History */}
                  {lifecycle?.length === 0 ? (
                    <div className="text-center py-24 text-slate-400">
                      <Icon name="GitBranch" size={28} className="mx-auto mb-8 opacity-30" />
                      <p className="text-sm">No lifecycle history yet. Add the first stage.</p>
                    </div>
                  ) : (
                    <div className="space-y-12">
                      <h3 className="text-sm font-semibold text-slate-700 mb-12">Stage History</h3>
                      {[...lifecycle]?.reverse()?.map((entry, idx) => {
                        const meta = STAGE_META?.[entry?.stage];
                        return (
                          <div key={entry?.id} className="flex items-start gap-14 p-16 bg-slate-50 rounded-xl">
                            <div className={`p-8 rounded-lg ${meta?.light || 'bg-slate-100 text-slate-600'} border flex-shrink-0`}>
                              <Icon name={meta?.icon || 'Circle'} size={14} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-10 flex-wrap">
                                <span className="text-sm font-semibold text-slate-800">{meta?.label || entry?.stage}</span>
                                <span className="text-xs text-slate-400">{formatDate(entry?.stageDate)}</span>
                                {entry?.changedByName && (
                                  <span className="text-xs text-slate-400">by {entry?.changedByName}</span>
                                )}
                              </div>
                              {entry?.notes && <p className="text-sm text-slate-500 mt-4">{entry?.notes}</p>}
                              {entry?.expectedEolDate && (
                                <p className="text-xs text-amber-600 mt-4 flex items-center gap-4">
                                  <Icon name="Calendar" size={11} />
                                  Expected EOL: {formatDate(entry?.expectedEolDate)}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Updates Tab ── */}
            {activeTab === 'updates' && (
              <div className="space-y-16">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900">Updates & News Feed</h2>
                  <button
                    onClick={() => setShowPostUpdate(true)}
                    className="flex items-center gap-8 px-16 py-9 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    <Icon name="Plus" size={15} />
                    Post Update
                  </button>
                </div>

                {updates?.length === 0 ? (
                  <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col items-center justify-center py-48 text-slate-400">
                    <Icon name="Rss" size={36} className="mb-12 opacity-30" />
                    <p className="font-medium">No updates yet</p>
                    <p className="text-sm mt-4">Post the first update for this service</p>
                  </div>
                ) : (
                  <div className="space-y-12">
                    {updates?.map(update => {
                      const meta = UPDATE_TYPE_META?.[update?.updateType] || UPDATE_TYPE_META?.general;
                      return (
                        <div key={update?.id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-20">
                          <div className="flex items-start justify-between gap-12">
                            <div className="flex items-start gap-14 flex-1 min-w-0">
                              <div className={`p-8 rounded-lg ${meta?.color} flex-shrink-0`}>
                                <Icon name={meta?.icon} size={14} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-10 mb-6">
                                  <span className={`inline-flex px-8 py-3 rounded-md text-xs font-medium ${meta?.color}`}>
                                    {meta?.label}
                                  </span>
                                  <span className="text-xs text-slate-400">{formatDate(update?.createdAt)}</span>
                                  <span className="text-xs text-slate-400">by {update?.authorName}</span>
                                </div>
                                <p className="text-sm font-semibold text-slate-800">{update?.title}</p>
                                {update?.content && (
                                  <p className="text-sm text-slate-500 mt-6 leading-relaxed">{update?.content}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              {deleteUpdateConfirm === update?.id ? (
                                <div className="flex items-center gap-6">
                                  <button
                                    onClick={() => handleDeleteUpdate(update?.id)}
                                    className="px-10 py-5 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
                                  >
                                    Delete
                                  </button>
                                  <button
                                    onClick={() => setDeleteUpdateConfirm(null)}
                                    className="px-10 py-5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setDeleteUpdateConfirm(update?.id)}
                                  className="p-8 rounded-lg text-slate-300 hover:text-red-400 hover:bg-red-50 transition-colors"
                                >
                                  <Icon name="Trash2" size={14} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {showPostUpdate && (
        <PostUpdateModal
          serviceId={serviceId}
          onClose={() => setShowPostUpdate(false)}
          onSuccess={(created) => {
            setUpdates(prev => [created, ...prev]);
            setShowPostUpdate(false);
            toast?.success('Update posted.');
          }}
        />
      )}

      {showAddStage && (
        <AddStageModal
          serviceId={serviceId}
          onClose={() => setShowAddStage(false)}
          onSuccess={() => {
            setShowAddStage(false);
            loadAll();
            toast?.success('Lifecycle stage added.');
          }}
        />
      )}
    </>
  );
}
